"use client"

import { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import { ArrowLeft, IndianRupee, Plus, Banknote, CalendarDays, Package, RefreshCw, Download } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { fetchCustomerLedger, createLedgerEntry, addPayment, fetchCustomerPaymentLogs } from "../../../services/ledgerService"

const STATUS_COLORS = {
  PAID: "bg-green-100 text-green-700 border border-green-200",
  PARTIAL: "bg-yellow-100 text-yellow-700 border border-yellow-200",
  PENDING: "bg-gray-100 text-gray-600 border border-gray-200",
  OVERDUE: "bg-red-100 text-red-700 border border-red-200",
}

const SOURCE_COLORS = {
  INVENTORY: "bg-blue-100 text-blue-700 border border-blue-200",
  MANUAL: "bg-gray-100 text-gray-600 border border-gray-200",
}

const SOURCE_ICONS = {
  INVENTORY: "📦",
  MANUAL: "✍️",
}

function fmt(n) {
  return (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })
}

function fmtDate(d) {
  if (!d) return "—"
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

// Bulk-issue entries store items as a single "Name x4, Name x2" string in `notes`
// (see back-end bulk issue: notes = itemNames). Split that back into per-item lines.
function parseItemLines(entry) {
  if (entry.inventory_item_name) {
    return [{ name: entry.inventory_item_name, qty: entry.quantity }]
  }
  if (entry.notes) {
    const parts = entry.notes.split(",").map((p) => p.trim()).filter(Boolean)
    const parsed = parts.map((p) => {
      const m = p.match(/^(.*)\sx(\d+(?:\.\d+)?)$/i)
      return m ? { name: m[1].trim(), qty: m[2] } : { name: p, qty: null }
    })
    if (parsed.every((p) => p.qty !== null)) return parsed
  }
  return [{ name: entry.notes || "—", qty: null }]
}

// Same split as above, but also carries SKU/unit-amount where the entry has
// single-item structured fields (bulk-issue notes-parsed lines have neither).
function parseBillingItemLines(entry) {
  if (entry.inventory_item_name) {
    const amount = entry.quantity && entry.total_amount ? entry.total_amount : null
    return [{
      sku: entry.inventory_item_id != null ? `#${entry.inventory_item_id}` : null,
      name: entry.inventory_item_name,
      qty: entry.quantity,
      amount,
    }]
  }
  return parseItemLines(entry).map((it) => ({ sku: null, name: it.name, qty: it.qty, amount: null }))
}

export default function CustomerLedgerDetail() {
  const { customerId } = useParams()
  const navigate = useNavigate()

  const [customer, setCustomer] = useState(null)
  const [entries, setEntries] = useState([])
  const [summary, setSummary] = useState({ total_due: 0, total_paid: 0, total_amount: 0 })
  const [loading, setLoading] = useState(true)

  const [showAddEntry, setShowAddEntry] = useState(false)
  const [newEntry, setNewEntry] = useState({ total_amount: "", paid_amount: "0", due_date: "", notes: "" })
  const [savingEntry, setSavingEntry] = useState(false)
  const [entryErrors, setEntryErrors] = useState({ total_amount: "", paid_amount: "" })

  const [payingEntry, setPayingEntry] = useState(null)
  const [payAmount, setPayAmount] = useState("")
  const [savingPay, setSavingPay] = useState(false)
  const [payError, setPayError] = useState("")

  // Ledger Slip — double-entry statement (Date/Particulars/Qty/Bill/Payment Made/Balance)
  const [showSlipDialog, setShowSlipDialog] = useState(false)
  const [slipFrom, setSlipFrom] = useState("")
  const [slipTo, setSlipTo] = useState("")
  const [slipLoading, setSlipLoading] = useState(false)

  // Billing Slip (SKU-level issue history)
  const [showBillingDialog, setShowBillingDialog] = useState(false)
  const [billingFrom, setBillingFrom] = useState("")
  const [billingTo, setBillingTo] = useState("")
  const [billingLoading, setBillingLoading] = useState(false)

  const handleDownloadBillingSlip = async () => {
    try {
      setBillingLoading(true)

      // Fetch payment logs for the date range
      const params = {}
      if (billingFrom) params.from = billingFrom
      if (billingTo) params.to = billingTo
      const { data: paymentLogs } = await fetchCustomerPaymentLogs(customerId, params)

      // Filter billing entries by date
      let filtered = [...entries]
      if (billingFrom) filtered = filtered.filter(e => new Date(e.created_at) >= new Date(billingFrom))
      if (billingTo) {
        const to = new Date(billingTo)
        to.setHours(23, 59, 59, 999)
        filtered = filtered.filter(e => new Date(e.created_at) <= to)
      }

      const dateRange = billingFrom || billingTo ? `${billingFrom || "—"} to ${billingTo || "—"}` : "All time"
      const fmtD = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"

      const totalAmt = filtered.reduce((s, e) => s + (e.total_amount || 0), 0)
      const totalPaid = filtered.reduce((s, e) => s + (e.paid_amount || 0), 0)
      const totalDue = filtered.reduce((s, e) => s + (e.due_amount || 0), 0)
      const totalPayReceived = paymentLogs.reduce((s, p) => s + (p.amount || 0), 0)

      // --- SECTION 1: Issued items, grouped by billing date, one item per line ---
      const byDate = {}
      filtered.forEach((e) => {
        const key = fmtD(e.created_at)
        if (!byDate[key]) byDate[key] = []
        byDate[key].push(e)
      })
      const billingSection = Object.entries(byDate).map(([date, dayEntries]) => {
        const txnBlocks = dayEntries.map((e) => {
          const txnRef = e.transaction_id != null ? `TXN #${e.transaction_id}` : `TXN #${String(e._id).slice(-6).toUpperCase()}`
          const itemRows = parseBillingItemLines(e).map((it) => `
            <div class="item-line">
              ${it.sku ? `<span class="item-sku">${it.sku}</span>` : ""}
              <span class="item-name">${it.name}</span>
              <span class="item-leader"></span>
              ${it.qty != null ? `<span class="item-qty">× ${it.qty}</span>` : ""}
              ${it.amount != null ? `<span class="item-amt">₹${fmt(it.amount)}</span>` : ""}
            </div>`).join("")
          return `
            <div class="txn-block">
              <div class="txn-head"><span class="txn-id">${txnRef}</span></div>
              ${itemRows}
              <div class="txn-foot">
                <div class="f"><span class="k">Amount</span><span class="v">₹${fmt(e.total_amount)}</span></div>
                <div class="f"><span class="k">Paid</span><span class="v paid">₹${fmt(e.paid_amount)}</span></div>
                <div class="f"><span class="k">Due</span><span class="v due">₹${fmt(e.due_amount)}</span></div>
                <div class="spacer"></div>
                <span class="status-chip ${e.status}">${e.status}</span>
              </div>
            </div>`
        }).join("")
        return `
          <div class="date-group">
            <div class="date-heading"><span class="d">${date}</span><span class="rule"></span></div>
            ${txnBlocks}
          </div>`
      }).join("")

      // --- SECTION 2: Payments received, grouped by payment date ---
      const byPayDate = {}
      paymentLogs.forEach((p) => {
        const key = fmtD(p.payment_date)
        if (!byPayDate[key]) byPayDate[key] = []
        byPayDate[key].push(p)
      })
      const paymentSection = Object.entries(byPayDate).map(([date, payments]) => {
        const payLines = payments.map((p) => `
          <div class="pay-line">
            <span class="pay-against">Against: ${p.ledger_entry?.inventory_item_name || p.notes || "—"}</span>
            <span class="pay-leader"></span>
            <span class="pay-amt">₹${fmt(p.amount)}</span>
            ${p.notes ? `<span class="pay-note">${p.notes}</span>` : ""}
          </div>`).join("")
        return `
          <div class="date-group">
            <div class="date-heading pay"><span class="d">${date}</span><span class="rule"></span></div>
            <div class="pay-block">${payLines}</div>
          </div>`
      }).join("")

      // --- SECTION 3: Summary ---
      const paidEntries = filtered.filter(e => e.status === "PAID")
      const partialEntries = filtered.filter(e => e.status === "PARTIAL")
      const pendingEntries = filtered.filter(e => e.status === "PENDING" || e.status === "OVERDUE")

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Billing Slip — ${customer?.name || ""}</title>
        <style>
          :root {
            --ink:#1a2129; --ink-soft:#3a4552; --muted:#64707d; --line:#dcdfe3; --line-soft:#e9ebee;
            --accent:#0263cc; --accent-soft:#eaf2fd; --paid:#1a7f4b; --paid-soft:#eaf6ef;
            --due:#b4392f; --due-soft:#fbebe9; --partial:#92600b; --partial-soft:#fbf1de;
            --ledger-rule: rgba(2,99,204,0.055); --ledger-margin: rgba(180,57,47,0.28);
          }
          * { box-sizing:border-box; }
          body {
            margin:0; font-family:ui-sans-serif,Arial,sans-serif; color:var(--ink);
            padding:36px 40px 36px 52px;
            background: linear-gradient(var(--ledger-rule) 1px, transparent 1px) 0 118px / 100% 27px, #fff;
            position:relative;
            -webkit-print-color-adjust:exact; print-color-adjust:exact;
          }
          body::before { content:""; position:absolute; top:0; bottom:0; left:36px; width:1px; background:var(--ledger-margin); }
          .masthead { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding-bottom:18px; border-bottom:2px solid var(--ink); }
          .slip-tag { display:inline-block; font-size:10.5px; letter-spacing:0.08em; text-transform:uppercase; color:var(--accent); background:var(--accent-soft); border-radius:999px; padding:3px 10px; margin-bottom:8px; font-weight:600; }
          .brand-name { font-family:Georgia,"Times New Roman",serif; font-size:24px; font-weight:600; margin:0 0 4px; }
          .brand-meta { display:flex; flex-wrap:wrap; gap:3px 14px; font-size:12.5px; color:var(--muted); }
          .brand-period { margin-top:6px; font-size:12px; color:var(--ink-soft); }
          .masthead-right { text-align:right; font-size:11px; color:var(--muted); white-space:nowrap; }
          .grand { display:grid; grid-template-columns:repeat(4,1fr); gap:1px; background:var(--line); border:1px solid var(--line); border-radius:8px; overflow:hidden; margin:20px 0 28px; }
          .grand-cell { background:#fff; padding:12px 14px; text-align:center; }
          .grand-label { font-size:10px; letter-spacing:0.06em; text-transform:uppercase; color:var(--muted); margin-bottom:4px; }
          .grand-val { font-size:17px; font-weight:600; }
          .grand-val.paid { color:var(--paid); }
          .grand-val.due { color:var(--due); }
          .section-title { display:flex; align-items:baseline; gap:8px; margin:30px 0 14px; }
          .section-title .num { font-size:11px; font-weight:700; color:var(--accent); background:var(--accent-soft); border-radius:5px; padding:2px 7px; }
          .section-title.payment .num { color:var(--paid); background:var(--paid-soft); }
          .section-title h2 { margin:0; font-size:14px; font-weight:700; letter-spacing:0.02em; }
          .section-title .rule { flex:1; height:1px; background:var(--line); }
          .date-group { margin-bottom:20px; }
          .date-group:last-child { margin-bottom:0; }
          .date-heading { display:flex; align-items:baseline; gap:10px; margin-bottom:10px; }
          .date-heading .d { font-size:12.5px; font-weight:700; letter-spacing:0.03em; color:var(--accent); text-transform:uppercase; }
          .date-heading.pay .d { color:var(--paid); }
          .date-heading .rule { flex:1; height:1px; background:var(--line); }
          .txn-block { border:1px solid var(--line); border-radius:8px; margin-bottom:10px; overflow:hidden; }
          .txn-block:last-child { margin-bottom:0; }
          .txn-head { display:flex; align-items:center; gap:8px; padding:8px 14px; border-bottom:1px solid var(--line-soft); }
          .txn-id { font-family:ui-monospace,"SF Mono",Consolas,monospace; font-size:11px; font-weight:600; color:var(--accent); background:var(--accent-soft); border-radius:4px; padding:1px 6px; }
          .item-line { display:flex; align-items:baseline; gap:8px; padding:8px 14px; font-size:13.5px; border-bottom:1px dashed var(--line-soft); }
          .item-line:last-of-type { border-bottom:none; }
          .item-sku { font-size:10.5px; color:var(--muted); background:var(--line-soft); border-radius:4px; padding:1px 5px; }
          .item-name { color:var(--ink-soft); }
          .item-leader { flex:1; border-bottom:1px dotted var(--line); transform:translateY(-3px); min-width:12px; }
          .item-qty { font-size:12.5px; color:var(--muted); white-space:nowrap; }
          .item-amt { font-size:12.5px; font-weight:600; color:var(--ink); white-space:nowrap; min-width:72px; text-align:right; }
          .txn-foot { display:flex; align-items:center; gap:18px; padding:9px 14px; background:var(--line-soft); font-size:12px; }
          .txn-foot .f { display:flex; gap:5px; align-items:baseline; }
          .txn-foot .f .k { color:var(--muted); font-size:10.5px; letter-spacing:0.03em; text-transform:uppercase; }
          .txn-foot .f .v { font-weight:600; }
          .txn-foot .v.paid { color:var(--paid); }
          .txn-foot .v.due { color:var(--due); }
          .txn-foot .spacer { flex:1; }
          .status-chip { font-size:10px; font-weight:700; letter-spacing:0.04em; padding:2px 9px; border-radius:999px; text-transform:uppercase; }
          .status-chip.PENDING { background:var(--line-soft); color:var(--muted); border:1px solid var(--line); }
          .status-chip.PARTIAL { background:var(--partial-soft); color:var(--partial); }
          .status-chip.PAID { background:var(--paid-soft); color:var(--paid); }
          .status-chip.OVERDUE { background:var(--due-soft); color:var(--due); }
          .pay-block { border:1px solid var(--line); border-radius:8px; overflow:hidden; }
          .pay-line { display:flex; align-items:baseline; gap:8px; padding:10px 14px; font-size:13.5px; border-bottom:1px dashed var(--line-soft); }
          .pay-line:last-of-type { border-bottom:none; }
          .pay-against { color:var(--ink-soft); }
          .pay-leader { flex:1; border-bottom:1px dotted var(--line); transform:translateY(-3px); min-width:12px; }
          .pay-amt { font-size:13px; font-weight:700; color:var(--paid); white-space:nowrap; }
          .pay-note { font-size:11px; color:var(--muted); font-style:italic; white-space:nowrap; }
          .status-row { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:14px; }
          .status-summary-chip { display:flex; align-items:baseline; gap:6px; padding:6px 12px; border-radius:999px; font-size:12px; font-weight:600; }
          .status-summary-chip.paid { background:var(--paid-soft); color:var(--paid); }
          .status-summary-chip.partial { background:var(--partial-soft); color:var(--partial); }
          .status-summary-chip.pending { background:var(--due-soft); color:var(--due); }
          .closing-total { margin-top:8px; background:var(--ink); color:#fff; border-radius:8px; padding:16px 20px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:16px; }
          .closing-total .group { display:flex; gap:28px; flex-wrap:wrap; }
          .closing-total .item .label { font-size:10px; letter-spacing:0.07em; text-transform:uppercase; opacity:0.7; margin-bottom:2px; }
          .closing-total .item .val { font-size:17px; font-weight:700; }
          .closing-total .item .val.due { color:#ff8f85; }
          .closing-total .item .val.paid { color:#7be0a6; }
          .footer { text-align:right; font-size:10.5px; color:var(--muted); margin-top:18px; }
          .empty { text-align:center; color:var(--muted); padding:24px 0; }
          @media print { body{padding:24px 28px 24px 40px;} }
        </style></head><body>
        <div class="masthead">
          <div>
            <div class="slip-tag">Billing Slip · SKU-level</div>
            <h1 class="brand-name">${customer?.name || "Customer"}</h1>
            <div class="brand-meta">${customer?.phone ? `<span>📞 ${customer.phone}</span>` : ""}${customer?.email ? `<span>✉ ${customer.email}</span>` : ""}</div>
            <div class="brand-period">Period: ${dateRange}</div>
          </div>
          <div class="masthead-right">Generated<br>${new Date().toLocaleString("en-IN")}</div>
        </div>
        <div class="grand">
          <div class="grand-cell"><div class="grand-label">Total Billed</div><div class="grand-val">₹${fmt(totalAmt)}</div></div>
          <div class="grand-cell"><div class="grand-label">Total Paid</div><div class="grand-val paid">₹${fmt(totalPaid)}</div></div>
          <div class="grand-cell"><div class="grand-label">Total Due</div><div class="grand-val due">₹${fmt(totalDue)}</div></div>
          <div class="grand-cell"><div class="grand-label">Transactions</div><div class="grand-val">${filtered.length}</div></div>
        </div>
        <div class="closing-total">
          <div class="group">
            <div class="item"><div class="label">Total Billed</div><div class="val">₹${fmt(totalAmt)}</div></div>
            <div class="item"><div class="label">Total Paid</div><div class="val paid">₹${fmt(totalPaid)}</div></div>
            <div class="item"><div class="label">Received in Period</div><div class="val paid">₹${fmt(totalPayReceived)}</div></div>
          </div>
          <div class="item"><div class="label">Outstanding</div><div class="val due">₹${fmt(totalDue)}</div></div>
        </div>

        <div class="section-title"><span class="num">1</span><h2>Issued Items — by billing date</h2><span class="rule"></span></div>
        ${billingSection || "<div class='empty'>No billing entries in selected range</div>"}

        <div class="section-title payment"><span class="num">2</span><h2>Payment History — by payment date</h2><span class="rule"></span></div>
        ${paymentSection || "<div class='empty'>No payments recorded in selected range</div>"}

        <div class="section-title"><span class="num">3</span><h2>Summary</h2><span class="rule"></span></div>
        <div class="status-row">
          <span class="status-summary-chip pending">${pendingEntries.length} pending — ₹${fmt(pendingEntries.reduce((s,e)=>s+e.total_amount,0))}</span>
          <span class="status-summary-chip partial">${partialEntries.length} partial — ₹${fmt(partialEntries.reduce((s,e)=>s+e.due_amount,0))} due</span>
          <span class="status-summary-chip paid">${paidEntries.length} fully paid</span>
        </div>
        <div class="footer">Clynox — Customer Inventory Billing Slip</div>
      </body></html>`

      const win = window.open("", "_blank")
      win.document.write(html)
      win.document.close()
      win.print()
      setShowBillingDialog(false)
    } catch {
      toast.error("Failed to generate billing slip")
    } finally {
      setBillingLoading(false)
    }
  }

  const handleDownloadSlip = async () => {
    try {
      setSlipLoading(true)
      // Unfiltered — need full history to compute both the opening balance
      // (movements before `from`) and in-period rows off the same data.
      const { data: paymentLogs } = await fetchCustomerPaymentLogs(customerId, {})

      // One row per entry: `paid_amount` minus whatever's already accounted
      // for by a real PaymentLog is the upfront portion (createLedgerEntry
      // writes paid_amount directly, with no PaymentLog row) — that upfront
      // amount lands in the same row as the bill, not a separate line.
      // Any later addPayment call *does* have its own PaymentLog + date, so
      // those become their own standalone payment rows.
      const rows = []
      entries.forEach((e) => {
        const matchingLogs = paymentLogs.filter((p) => String(p.ledger_entry?._id || p.ledger_entry || "") === String(e._id))
        const loggedTotal = matchingLogs.reduce((s, p) => s + (p.amount || 0), 0)
        const upfront = Math.max(0, (e.paid_amount || 0) - loggedTotal)
        rows.push({ date: new Date(e.created_at), kind: "entry", entry: e, bill: e.total_amount || 0, paymentMade: upfront })
        matchingLogs.forEach((p) => {
          rows.push({ date: new Date(p.payment_date), kind: "payment", entry: e, paymentLog: p, bill: 0, paymentMade: p.amount || 0 })
        })
      })
      rows.sort((a, b) => a.date - b.date)

      const fromDate = slipFrom ? new Date(slipFrom) : null
      let toDate = null
      if (slipTo) {
        toDate = new Date(slipTo)
        toDate.setHours(23, 59, 59, 999)
      }

      const before = fromDate ? rows.filter((r) => r.date < fromDate) : []
      const inPeriod = rows.filter((r) => (!fromDate || r.date >= fromDate) && (!toDate || r.date <= toDate))

      const openingBalance = before.reduce((s, r) => s + r.bill - r.paymentMade, 0)
      const dateRange = slipFrom || slipTo ? `${slipFrom || "—"} to ${slipTo || "—"}` : "All time"

      let running = openingBalance
      const bodyRows = inPeriod.map((r) => {
        running += r.bill - r.paymentMade
        return { ...r, balance: running }
      })

      const totalBill = inPeriod.reduce((s, r) => s + r.bill, 0)
      const totalPayment = inPeriod.reduce((s, r) => s + r.paymentMade, 0)
      const closingBalance = running

      const balHtml = (v) => `${fmt(Math.abs(v))}<span class="suffix">${v >= 0 ? "Dr" : "Cr"}</span>`

      const rowsHtml = bodyRows.map((r) => {
        if (r.kind === "payment") {
          return `
            <tr>
              <td class="col-date">${fmtDate(r.date)}</td>
              <td><span class="src-label">Payment Received${r.paymentLog.notes ? " — " + r.paymentLog.notes : ""}</span></td>
              <td class="num muted-dash">—</td>
              <td class="num muted-dash">—</td>
              <td class="num payment-val">${fmt(r.paymentMade)}</td>
              <td class="num balance-val">${balHtml(r.balance)}</td>
            </tr>`
        }
        const e = r.entry
        const items = parseItemLines(e)
        const txnRef = e.transaction_id != null ? `TXN #${e.transaction_id}` : `TXN #${String(e._id).slice(-6).toUpperCase()}`
        const srcText = e.source === "INVENTORY" ? "Inventory Issue" : "Manual Entry"
        const itemNames = items.map((it) => `<div class="item-name">${it.name}</div>`).join("")
        const itemQtys = items.map((it) => `<span class="q">${it.qty ?? "—"}</span>`).join("")
        return `
          <tr>
            <td class="col-date">${fmtDate(r.date)}</td>
            <td>
              <div class="ref-line">
                <span class="txn-id">${txnRef}</span>
                <span class="src-label">${SOURCE_ICONS[e.source || "MANUAL"]} ${srcText}</span>
                <span class="status-chip ${e.status}">${e.status}</span>
              </div>
              <div class="item-grid">${itemNames}</div>
            </td>
            <td class="qty-col"><div class="qty-grid">${itemQtys}</div></td>
            <td class="num bill-val">${fmt(r.bill)}</td>
            <td class="num ${r.paymentMade > 0 ? "payment-val" : "muted-dash"}">${r.paymentMade > 0 ? fmt(r.paymentMade) : "—"}</td>
            <td class="num balance-val">${balHtml(r.balance)}</td>
          </tr>`
      }).join("")

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ledger Statement - ${customer?.name || ""}</title>
        <style>
          :root {
            --ink:#1a2129; --ink-soft:#3a4552; --muted:#64707d; --line:#dcdfe3; --line-soft:#e9ebee;
            --accent:#0263cc; --accent-soft:#eaf2fd; --paid:#1a7f4b; --paid-soft:#eaf6ef;
            --due:#b4392f; --due-soft:#fbebe9; --partial:#92600b; --partial-soft:#fbf1de;
            --ledger-rule: rgba(2,99,204,0.05); --ledger-margin: rgba(180,57,47,0.28);
          }
          * { box-sizing:border-box; }
          body {
            margin:0; font-family:ui-sans-serif,Arial,sans-serif; color:var(--ink);
            padding:36px 40px 36px 54px;
            background: linear-gradient(var(--ledger-rule) 1px, transparent 1px) 0 150px / 100% 40px, #fff;
            position:relative;
            -webkit-print-color-adjust:exact; print-color-adjust:exact;
          }
          body::before { content:""; position:absolute; top:0; bottom:0; left:38px; width:1px; background:var(--ledger-margin); }
          .masthead { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding-bottom:16px; border-bottom:2px solid var(--ink); }
          .slip-tag { display:inline-block; font-size:10.5px; letter-spacing:0.08em; text-transform:uppercase; color:var(--accent); background:var(--accent-soft); border-radius:999px; padding:3px 10px; margin-bottom:8px; font-weight:600; }
          .brand-name { font-family:Georgia,"Times New Roman",serif; font-size:24px; font-weight:600; margin:0 0 4px; }
          .brand-meta { display:flex; flex-wrap:wrap; gap:3px 14px; font-size:12.5px; color:var(--muted); }
          .masthead-right { text-align:right; font-size:11px; color:var(--muted); white-space:nowrap; }
          .masthead-right .period { margin-top:8px; font-size:12px; color:var(--ink-soft); }
          .table-wrap { overflow-x:auto; margin-top:20px; }
          table.ledger { width:100%; border-collapse:collapse; font-size:13px; table-layout:fixed; }
          .ledger thead th { text-align:left; font-size:10.5px; letter-spacing:0.05em; text-transform:uppercase; color:var(--muted); font-weight:700; padding:0 10px 8px; border-bottom:2px solid var(--ink); white-space:nowrap; }
          .ledger th.num, .ledger td.num { text-align:right; }
          .ledger td { padding:10px; border-bottom:1px solid var(--line-soft); vertical-align:top; }
          .ledger tr.opening td { color:var(--muted); font-style:italic; }
          .col-date { white-space:nowrap; color:var(--ink-soft); font-size:12.5px; }
          .ref-line { display:flex; align-items:center; gap:8px; margin-bottom:6px; flex-wrap:wrap; }
          .txn-id { font-family:ui-monospace,"SF Mono",Consolas,monospace; font-size:11px; font-weight:600; color:var(--accent); background:var(--accent-soft); border-radius:4px; padding:1px 6px; }
          .src-label { font-size:12px; color:var(--muted); }
          .item-grid { display:flex; flex-direction:column; row-gap:3px; }
          .item-name { font-size:12.5px; color:var(--ink-soft); line-height:1.5; }
          .qty-col { text-align:right; }
          .qty-grid { display:flex; flex-direction:column; row-gap:3px; align-items:flex-end; }
          .qty-grid .q { font-family:ui-monospace,"SF Mono",Consolas,monospace; font-size:12.5px; color:var(--muted); line-height:1.5; }
          .status-chip { font-size:9.5px; font-weight:700; letter-spacing:0.04em; padding:1px 8px; border-radius:999px; text-transform:uppercase; white-space:nowrap; }
          .status-chip.PENDING { background:var(--line-soft); color:var(--muted); border:1px solid var(--line); }
          .status-chip.PARTIAL { background:var(--partial-soft); color:var(--partial); }
          .status-chip.PAID { background:var(--paid-soft); color:var(--paid); }
          .status-chip.OVERDUE { background:var(--due-soft); color:var(--due); }
          .bill-val { color:var(--ink); font-weight:600; }
          .payment-val { color:var(--paid); font-weight:600; }
          .balance-val { font-weight:700; color:var(--due); white-space:nowrap; }
          .balance-val .suffix { font-size:10px; font-weight:600; color:var(--muted); margin-left:2px; }
          .muted-dash { color:var(--muted); }
          .ledger tfoot td { border-top:2px solid var(--ink); border-bottom:none; padding-top:12px; font-weight:700; }
          .ledger tfoot .tot-label { color:var(--muted); font-size:10.5px; letter-spacing:0.05em; text-transform:uppercase; font-weight:700; }
          .closing-total { margin-top:22px; background:var(--ink); color:#fff; border-radius:8px; padding:14px 20px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:16px; }
          .closing-total .group { display:flex; gap:26px; flex-wrap:wrap; }
          .closing-total .item .label { font-size:10px; letter-spacing:0.07em; text-transform:uppercase; opacity:0.7; margin-bottom:2px; }
          .closing-total .item .val { font-size:17px; font-weight:700; }
          .closing-total .item .val.payment { color:#7be0a6; }
          .closing-total .item .val.balance { color:#ff8f85; }
          .footer { text-align:right; font-size:10.5px; color:var(--muted); margin-top:18px; }
          @media print { body{padding:24px 28px 24px 40px;} }
        </style></head><body>
        <div class="masthead">
          <div>
            <div class="slip-tag">Customer Ledger Statement</div>
            <h1 class="brand-name">${customer?.name || "Customer"}</h1>
            <div class="brand-meta">${customer?.phone ? `<span>📞 ${customer.phone}</span>` : ""}${customer?.email ? `<span>✉ ${customer.email}</span>` : ""}</div>
          </div>
          <div class="masthead-right">
            Generated ${new Date().toLocaleString("en-IN")}
            <div class="period">Period: ${dateRange}</div>
          </div>
        </div>
        <div class="closing-total">
          <div class="group">
            <div class="item"><div class="label">Total Bill</div><div class="val">₹${fmt(totalBill)}</div></div>
            <div class="item"><div class="label">Total Payment Made</div><div class="val payment">₹${fmt(totalPayment)}</div></div>
          </div>
          <div class="item"><div class="label">Closing Balance</div><div class="val balance">₹${balHtml(closingBalance)}</div></div>
        </div>
        <div class="table-wrap">
        <table class="ledger">
          <thead>
            <tr>
              <th style="width:88px">Date</th>
              <th>Particulars</th>
              <th class="num" style="width:70px">Qty</th>
              <th class="num" style="width:110px">Bill (₹)</th>
              <th class="num" style="width:130px">Payment Made (₹)</th>
              <th class="num" style="width:130px">Balance (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr class="opening">
              <td class="col-date">${fromDate ? fmtDate(fromDate) : "—"}</td>
              <td>Opening Balance b/f</td>
              <td class="num">—</td>
              <td class="num">—</td>
              <td class="num">—</td>
              <td class="num balance-val">${balHtml(openingBalance)}</td>
            </tr>
            ${rowsHtml}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3" class="tot-label">Totals</td>
              <td class="num bill-val">${fmt(totalBill)}</td>
              <td class="num payment-val">${fmt(totalPayment)}</td>
              <td class="num balance-val">${balHtml(closingBalance)}</td>
            </tr>
          </tfoot>
        </table>
        </div>
        <div class="footer">Clynox — Customer Ledger Statement</div>
      </body></html>`

      const win = window.open("", "_blank")
      win.document.write(html)
      win.document.close()
      win.print()
      setShowSlipDialog(false)
    } catch {
      toast.error("Failed to generate ledger statement")
    } finally {
      setSlipLoading(false)
    }
  }

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await fetchCustomerLedger(customerId)
      setCustomer(data.customer)
      setEntries(data.data || [])
      setSummary(data.summary || { total_due: 0, total_paid: 0, total_amount: 0 })
    } catch {
      toast.error("Failed to load customer ledger")
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => { load() }, [load])

  const handleAddEntry = async () => {
    const total = parseFloat(newEntry.total_amount)
    const paid = parseFloat(newEntry.paid_amount) || 0

    // Validate total amount
    if (!Number.isFinite(total)) {
      toast.error("Enter a valid total amount")
      return
    }
    if (total < 0) {
      toast.error("Total amount cannot be negative")
      return
    }
    if (total === 0) {
      toast.error("Total amount must be greater than 0")
      return
    }

    // Validate paid amount
    if (!Number.isFinite(paid)) {
      toast.error("Enter a valid paid amount")
      return
    }
    if (paid < 0) {
      toast.error("Paid amount cannot be negative")
      return
    }
    if (paid > total) {
      toast.error(`Paid amount (₹${paid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}) cannot exceed total amount (₹${total.toLocaleString("en-IN", { minimumFractionDigits: 2 })})`)
      return
    }
    try {
      setSavingEntry(true)
      await createLedgerEntry({
        customer_id: customerId,
        total_amount: total,
        paid_amount: paid,
        due_date: newEntry.due_date || null,
        notes: newEntry.notes,
      })
      toast.success("Ledger entry added")
      setShowAddEntry(false)
      setNewEntry({ total_amount: "", paid_amount: "0", due_date: "", notes: "" })
      load()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add entry")
    } finally {
      setSavingEntry(false)
    }
  }

  const validatePayAmount = (value, dueAmount) => {
    if (!value || value === "") {
      return "Payment amount is required"
    }
    const amount = parseFloat(value)
    if (!Number.isFinite(amount)) {
      return "Enter a valid payment amount"
    }
    if (amount <= 0) {
      return "Payment amount must be greater than 0"
    }
    if (dueAmount !== undefined && amount > dueAmount) {
      return `Amount cannot exceed due amount (₹${dueAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })})`
    }
    return ""
  }

  const handlePayAmountChange = (e) => {
    const value = e.target.value
    setPayAmount(value)
    const error = validatePayAmount(value, payingEntry?.due_amount)
    setPayError(error)
  }

  const isPayAmountValid = () => {
    const amount = parseFloat(payAmount)
    return Number.isFinite(amount) && amount > 0 && amount <= (payingEntry?.due_amount || 0)
  }

  const handleAddPayment = async () => {
    const amount = parseFloat(payAmount)

    // Final validation before API call
    const error = validatePayAmount(payAmount, payingEntry?.due_amount)
    if (error) {
      toast.error(error)
      setPayError(error)
      return
    }

    try {
      setSavingPay(true)
      await addPayment(payingEntry._id, amount)
      toast.success("Payment recorded successfully")
      setPayingEntry(null)
      setPayAmount("")
      setPayError("")
      load()
    } catch (err) {
      const backendMessage = err?.response?.data?.message
      const backendDetails = err?.response?.data?.details

      if (backendMessage) {
        toast.error(backendMessage)
        if (backendDetails?.due_amount !== undefined) {
          setPayError(`Due amount is ₹${backendDetails.due_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`)
        }
      } else {
        toast.error("Failed to record payment")
      }
    } finally {
      setSavingPay(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Back + Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/customer-ledger")} className="mt-0.5">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{customer?.name || "Customer"}</h1>
          <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
            {customer?.phone && <span>📞 {customer.phone}</span>}
            {customer?.email && <span>✉️ {customer.email}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSlipDialog(true)} className="flex items-center gap-1.5 text-[#0263cc] border-[#0263cc]/30 hover:bg-[#0263cc]/5">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Ledger Slip</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowBillingDialog(true)} className="flex items-center gap-1.5 text-[#0263cc] border-[#0263cc]/50 hover:bg-[#0263cc]/5 font-medium">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Billing Slip</span>
          </Button>
          <Button size="sm" onClick={() => setShowAddEntry(true)} className="flex items-center gap-1.5">
            <Plus className="w-4 h-4" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 font-medium mb-1">Total Amount</p>
          <p className="text-xl font-bold text-gray-900">₹{fmt(summary.total_amount)}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 font-medium mb-1">Total Paid</p>
          <p className="text-xl font-bold text-green-600">₹{fmt(summary.total_paid)}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500 font-medium mb-1">Total Due</p>
          <p className="text-xl font-bold text-red-600">₹{fmt(summary.total_due)}</p>
        </div>
      </div>

      {/* Transactions */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-gray-800">Transactions ({entries.length})</h2>
        {entries.length === 0 ? (
          <div className="bg-white rounded-xl border py-12 text-center text-sm text-gray-400">
            No transactions yet.
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry._id} className="bg-white rounded-xl border p-4">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[entry.status]}`}>
                      {entry.status}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_COLORS[entry.source || "MANUAL"]}`}>
                      <span>{SOURCE_ICONS[entry.source || "MANUAL"]}</span>
                      {entry.source || "MANUAL"}
                    </span>
                    {entry.inventory_item_name && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        <Package className="w-3 h-3" />
                        {entry.inventory_item_name} × {entry.quantity}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-400">Total</p>
                      <p className="font-semibold text-gray-800">₹{fmt(entry.total_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Paid</p>
                      <p className="font-semibold text-green-600">₹{fmt(entry.paid_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Due</p>
                      <p className="font-semibold text-red-600">₹{fmt(entry.due_amount)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" />
                      Added {fmtDate(entry.created_at)}
                    </span>
                    {entry.due_date && (
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" />
                        Due {fmtDate(entry.due_date)}
                      </span>
                    )}
                  </div>
                  {entry.notes && (
                    entry.notes.startsWith("Returned ") ? (
                      <p className="inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-lg text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                        <span>↩</span>
                        {entry.notes}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 italic">{entry.notes}</p>
                    )
                  )}
                </div>
                {entry.due_amount > 0 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1.5 text-green-700 border-green-200 hover:bg-green-50 shrink-0"
                    onClick={() => { setPayingEntry(entry); setPayAmount(""); setPayError("") }}
                  >
                    <Banknote className="w-4 h-4" />
                    Pay
                  </Button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full shrink-0">
                    <span>✓</span> Paid
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Entry Dialog */}
      <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Ledger Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Total Amount *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className={`pl-8 ${entryErrors.total_amount ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  placeholder="0.00"
                  value={newEntry.total_amount}
                  onChange={(e) => {
                    const value = e.target.value
                    setNewEntry({ ...newEntry, total_amount: value })
                    const total = parseFloat(value)
                    const paid = parseFloat(newEntry.paid_amount) || 0
                    let error = ""
                    if (value && !Number.isFinite(total)) error = "Invalid amount"
                    else if (total < 0) error = "Cannot be negative"
                    else if (total === 0) error = "Must be greater than 0"
                    else if (paid > total) error = "Paid exceeds total"
                    setEntryErrors(prev => ({ ...prev, total_amount: error }))
                    if (paid > 0 && paid <= total) {
                      setEntryErrors(prev => ({ ...prev, paid_amount: "" }))
                    }
                  }}
                />
              </div>
              {entryErrors.total_amount && (
                <p className="text-xs text-red-600">{entryErrors.total_amount}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Paid Upfront</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className={`pl-8 ${entryErrors.paid_amount ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  placeholder="0.00"
                  value={newEntry.paid_amount}
                  onChange={(e) => {
                    const value = e.target.value
                    setNewEntry({ ...newEntry, paid_amount: value })
                    const paid = parseFloat(value) || 0
                    const total = parseFloat(newEntry.total_amount) || 0
                    let error = ""
                    if (value && !Number.isFinite(paid)) error = "Invalid amount"
                    else if (paid < 0) error = "Cannot be negative"
                    else if (total > 0 && paid > total) error = "Cannot exceed total amount"
                    setEntryErrors(prev => ({ ...prev, paid_amount: error }))
                  }}
                />
              </div>
              {entryErrors.paid_amount && (
                <p className="text-xs text-red-600">{entryErrors.paid_amount}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={newEntry.due_date}
                onChange={(e) => setNewEntry({ ...newEntry, due_date: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                placeholder="Optional notes"
                value={newEntry.notes}
                onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddEntry(false); setEntryErrors({ total_amount: "", paid_amount: "" }) }}>Cancel</Button>
            <Button
              onClick={handleAddEntry}
              disabled={savingEntry || entryErrors.total_amount || entryErrors.paid_amount || !newEntry.total_amount}
            >
              {savingEntry ? "Saving..." : "Add Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Slip Dialog */}
      <Dialog open={showSlipDialog} onOpenChange={setShowSlipDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Download Slip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-500">Double-entry statement — Date, Particulars, Qty, Bill, Payment Made, running Balance. Date range select karo ya blank chhodo sab transactions ke liye.</p>
            <div className="space-y-1.5">
              <Label>From Date</Label>
              <Input type="date" value={slipFrom} onChange={(e) => setSlipFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>To Date</Label>
              <Input type="date" value={slipTo} onChange={(e) => setSlipTo(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSlipDialog(false)}>Cancel</Button>
            <Button onClick={handleDownloadSlip} disabled={slipLoading} className="bg-[#0263cc] hover:bg-[#0263cc]/90 flex items-center gap-1.5">
              {slipLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Slip Dialog (SKU-level) */}
      <Dialog open={showBillingDialog} onOpenChange={setShowBillingDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Download Billing Slip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-500">SKU-wise billing history for this customer, grouped by date.</p>
            <div className="space-y-1.5">
              <Label>From Date</Label>
              <Input type="date" value={billingFrom} onChange={(e) => setBillingFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>To Date</Label>
              <Input type="date" value={billingTo} onChange={(e) => setBillingTo(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBillingDialog(false)}>Cancel</Button>
            <Button onClick={handleDownloadBillingSlip} disabled={billingLoading} className="bg-[#0263cc] hover:bg-[#0263cc]/90 flex items-center gap-1.5">
              {billingLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={!!payingEntry} onOpenChange={(open) => { if (!open) { setPayingEntry(null); setPayAmount(""); setPayError("") } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Outstanding due:</span>
              <span className="font-semibold text-red-600">₹{fmt(payingEntry?.due_amount)}</span>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Amount *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  min="0.01"
                  max={payingEntry?.due_amount}
                  step="0.01"
                  className={`pl-8 ${payError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  placeholder="0.00"
                  value={payAmount}
                  onChange={handlePayAmountChange}
                  autoFocus
                />
              </div>
              {payError && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <span className="inline-block">⚠️</span> {payError}
                </p>
              )}
              {!payError && payAmount && (
                <p className="text-xs text-gray-500">
                  After payment: ₹{fmt(Math.max(0, (payingEntry?.due_amount || 0) - parseFloat(payAmount || 0)))} remaining
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPayingEntry(null); setPayAmount(""); setPayError("") }}>Cancel</Button>
            <Button
              onClick={handleAddPayment}
              disabled={savingPay || !isPayAmountValid()}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingPay ? "Saving..." : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
